interface DerivedEntityPath {
  /** The path string passed to `useResolvedPath` (bare slug for the org root,
   * slash-prefixed for entity paths). */
  path: string;
  /** Entity path below the owner namespace. */
  effectiveSplat?: string;
}

/**
 * Derive the resolve-path query input from a route owner + splat. Used by the
 * `$owner/$` route's component (which renders the entity) and its loader (which
 * fire-and-forget prefetches the same query so the resolve is warm before the
 * component mounts). Keeping the derivation in one place guarantees the prefetch
 * key matches the key the component reads.
 */
export function deriveEntityResolvePath(owner: string, splat?: string): DerivedEntityPath {
  const effectiveSplat = splat;

  // Namespace root resolves as the bare owner slug, while entity paths use slash prefixes.
  const path = !effectiveSplat ? owner : `/${[owner, effectiveSplat].filter(Boolean).join('/')}`;

  return { path, effectiveSplat };
}

/**
 * The router decodes `_splat` per segment, so a `%2F` inside a source key would
 * read as a path boundary. Read the raw pathname instead: on org subdomains the
 * router pathname is still owner-prefixed (see `subdomain.ts`), so dropping the
 * leading owner segment is uniform across hosts.
 */
export function entitySplatFromPathname(pathname: string, sectionSegments = 0): string {
  return pathname.split('/').slice(2 + sectionSegments).join('/');
}

/** UI state must distinguish equal source keys in different entity types. */
export function entityStateKey(entity: { id: number | string | null; entity_type: string; slug: string }): number | string {
  return typeof entity.id === 'number' ? entity.id : JSON.stringify([entity.entity_type, entity.id ?? entity.slug]);
}
